/**
 * Pinned under the top bar: the secure links staff sent this client (ACTION_LINKS_PLAN.md), one row each,
 * until done, expired or withdrawn. They can't be dismissed. A finished one says thanks for a moment.
 */
import { Link, usePath } from '../lib/router';
import { useActionLinks, useActionLinkThanks } from '../stores/actionLinks';
import { Icon } from './ui';

export function timeLeft(expiresAt: string, now = Date.now()): string {
  const min = Math.max(0, Math.round((Date.parse(expiresAt) - now) / 60_000));
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} days`;
}

export function ActionLinkBar() {
  const links = useActionLinks().filter((l) => Date.parse(l.expiresAt) > Date.now());
  const thanked = useActionLinkThanks();
  const path = usePath();
  // Opening a link already lands on its page; the card stays everywhere else until it's done.
  if (path.startsWith('/l/')) return null;
  if (!links.length && !thanked) return null;
  return (
    <div role="region" aria-label="Action needed">
      {links.map((l) => (
        <div key={l.id} className="flex items-center justify-center gap-x-3 gap-y-1 bg-accent-bg px-4 py-2 text-center text-[13px] text-accent-fg max-sm:flex-col">
          {/* One line on desktop; on a phone it wraps as text instead of squeezing into columns. */}
          <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
            <Icon name="shield" size={13} />
            <span className="font-semibold tracking-[0.04em] uppercase">Action needed</span>
            <span className="font-medium">{l.title}</span>
            <span className="whitespace-nowrap tabular-nums opacity-80">· expires in {timeLeft(l.expiresAt)}</span>
          </span>
          <Link to={l.path} className="font-semibold text-fg underline underline-offset-4">
            {l.type === 'custom' ? 'Open' : 'Continue'}
          </Link>
        </div>
      ))}
      {thanked && (
        <div role="status" className="flex items-center justify-center gap-2 bg-up-bg px-4 py-2 text-[13px] font-medium text-up">
          <Icon name="check" size={13} />
          Thanks, we've got it. Nothing more to do.
        </div>
      )}
    </div>
  );
}
